import React, { useMemo } from 'react';
import BookCard from './BookCard';
import './RecentlyFinishedBooks.css';

function RecentlyFinishedBooks({ books, limit = 5 }) {
  const recentFinishedBooks = useMemo(() => {
    const finishedBooks = books.filter(book => 
      (book.readingStatus || book.status) === 'finished' && book.finishedReadingAt
    );

    return finishedBooks
      .sort((a, b) => {
        const dateA = a.finishedReadingAt?.seconds || 0;
        const dateB = b.finishedReadingAt?.seconds || 0;
        return dateB - dateA;
      })
      .slice(0, limit);
  }, [books, limit]);

  const getTimeAgo = (finishedDate) => {
    if (!finishedDate?.seconds) return 'Recently';
    
    const now = new Date();
    const bookDate = new Date(finishedDate.seconds * 1000);
    const diffInHours = Math.floor((now - bookDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just finished';
    if (diffInHours < 24) return `Finished ${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `Finished ${diffInDays}d ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `Finished ${diffInWeeks}w ago`;
    
    const diffInMonths = Math.floor(diffInDays / 30);
    return `Finished ${diffInMonths}mo ago`;
  };

  const calculateReadingTime = (book) => {
    if (!book.createdAt?.seconds || !book.finishedReadingAt?.seconds) {
      return null;
    }

    const startDate = new Date(book.createdAt.seconds * 1000);
    const finishDate = new Date(book.finishedReadingAt.seconds * 1000);
    const diffInDays = Math.floor((finishDate - startDate) / (1000 * 60 * 60 * 24));
    
    if (diffInDays < 1) return '< 1 day';
    if (diffInDays === 1) return '1 day';
    if (diffInDays < 30) return `${diffInDays} days`;
    
    const months = Math.floor(diffInDays / 30);
    const remainingDays = diffInDays % 30;
    
    if (months === 1 && remainingDays === 0) return '1 month';
    if (remainingDays === 0) return `${months} months`;
    return `${months}m ${remainingDays}d`;
  };

  if (recentFinishedBooks.length === 0) {
    const totalFinished = books.filter(book => 
      (book.readingStatus || book.status) === 'finished'
    ).length;

    return (
      <div className="recently-finished-books">
        <div className="section-header">
          <h2>🎉 Recently Finished</h2>
          <p>Books you've completed reading</p>
        </div>
        <div className="empty-finished">
          <div className="empty-icon">🏆</div>
          <p>
            {totalFinished > 0 
              ? "No recently finished books with completion dates."
              : "No finished books yet. Complete your first book to see it here!"
            }
          </p>
          {totalFinished > 0 && (
            <small>You have {totalFinished} finished book{totalFinished !== 1 ? 's' : ''} total.</small>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="recently-finished-books">
      <div className="section-header">
        <h2>🎉 Recently Finished</h2>
        <p>Latest reading accomplishments</p>
      </div>
      
      <div className="finished-books-list">
        {recentFinishedBooks.map((book, index) => (
          <div key={book.id} className="finished-book-item" style={{
            animationDelay: `${index * 0.1}s`
          }}>
            <div className="book-cover-section">
              <BookCard book={book} />
              <div className="completion-badge">
                <span className="badge-icon">✅</span>
                <span className="badge-text">Completed</span>
              </div>
            </div>
            
            <div className="book-details-section">
              <div className="book-info">
                <h4 className="book-title">{book.title}</h4>
                {book.authors && (
                  <p className="book-author">
                    by {Array.isArray(book.authors) ? book.authors.join(', ') : book.authors}
                  </p>
                )}
                
                <div className="reading-stats">
                  <div className="stat-item">
                    <span className="stat-icon">⏱️</span>
                    <span className="stat-text">{getTimeAgo(book.finishedReadingAt)}</span>
                  </div>
                  
                  {calculateReadingTime(book) && (
                    <div className="stat-item">
                      <span className="stat-icon">📚</span>
                      <span className="stat-text">Read in {calculateReadingTime(book)}</span>
                    </div>
                  )}
                  
                  {book.pageCount && (
                    <div className="stat-item">
                      <span className="stat-icon">📄</span>
                      <span className="stat-text">{book.pageCount} pages</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="achievement-section">
                <div className="achievement-icon">🏆</div>
                <div className="achievement-message">
                  <span>Great job!</span>
                  <small>Another book conquered</small>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="completion-summary">
        <div className="summary-card">
          <div className="summary-icon">📚</div>
          <div className="summary-stats">
            <span className="total-finished">
              {books.filter(book => (book.readingStatus || book.status) === 'finished').length}
            </span>
            <span className="summary-label">Books Completed</span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">📄</div>
          <div className="summary-stats">
            <span className="total-pages">
              {books
                .filter(book => (book.readingStatus || book.status) === 'finished')
                .reduce((total, book) => total + (book.pageCount || 0), 0)
                .toLocaleString()}
            </span>
            <span className="summary-label">Pages Read</span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">🎯</div>
          <div className="summary-stats">
            <span className="completion-rate">
              {books.length > 0 
                ? Math.round((books.filter(book => (book.readingStatus || book.status) === 'finished').length / books.length) * 100)
                : 0}%
            </span>
            <span className="summary-label">Completion Rate</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RecentlyFinishedBooks;